import React from "react";
import { Split } from "lucide-react";
import Logo from "@/shared/components/Logo";
import { cn } from "@/shared/lib/cn";
import { claudeCodeColor, providerPaths } from "./logos/paths";
import type { ProviderId } from "@/shared/types/models";

type Size = "md" | "sm" | "xs";

/* `md` the settings rows' 24px, `sm` the model picker's 20px rows, `xs` the composer strip's 16px:
   the box, the mark in it, Claude Code's tile and crab, and Definable's logo in px. One record, not
   CVA (rule book: "Variants via CVA"): a size sets four elements and a pixel number CVA can't hold. */
const SIZES: Record<Size, { box: string; glyph: string; tile: string; crab: string; logo: number }> = {
  md: { box: "w-6 h-6", glyph: "w-[18px] h-[18px]", tile: "rounded-[6px]", crab: "w-4 h-4", logo: 18 },
  sm: { box: "w-5 h-5", glyph: "w-[18px] h-[18px]", tile: "rounded-[5px]", crab: "w-[14px] h-[14px]", logo: 18 },
  xs: { box: "w-4 h-4", glyph: "w-[14px] h-[14px]", tile: "rounded-[4px]", crab: "w-[11px] h-[11px]", logo: 14 },
};

/** A provider's brand mark; Definable's own when there is no provider to show (nothing chosen yet).
    TypeSafe has no mark in Lobe Icons yet: a fork, for the decisions its model makes. Claude Code's
    crab sits white in a box of its colour, so it never reads as Claude's spark beside it. */
export default function ProviderMark({ provider, size = "md" }: { provider: ProviderId | null; size?: Size }) {
  const look = SIZES[size];
  const path = (d: string, className: string) =>
    <svg viewBox="0 0 24 24" fill="currentColor" fillRule="evenodd" focusable="false" className={className}><path d={d} /></svg>;
  return <span aria-hidden="true" className={cn("shrink-0 flex items-center justify-center text-[var(--text-primary)]", look.box)}>
    {provider === "claude_code"
      ? <span className={cn("w-full h-full flex items-center justify-center text-white", look.tile)} style={{ background: claudeCodeColor }}>{path(providerPaths.claude_code, look.crab)}</span>
      : provider === "typesafe" ? <Split className={look.glyph} strokeWidth={1.75} />
      : provider ? path(providerPaths[provider], look.glyph)
      : <Logo size={look.logo} />}
  </span>;
}
